import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import { getPublicOrder, getPublicPaymentSettings, uploadPaymentProof, createKpayCheckout, getKpayOrderStatus } from '../lib/api';
import type { OrderWithItems, PaymentSettings } from '../lib/api';
import { formatPrice } from '../lib/formatPrice';
import { Loader2, AlertCircle, CheckCircle2, Upload, FileText, ChevronLeft, Copy, CreditCard, Clock } from 'lucide-react';

const OrderConfirmationPage: React.FC = () => {
  const { t } = useTranslation();
  const { orderNum } = useParams<{ orderNum: string }>();

  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // KPay hosted cashier (shown only when enabled in admin settings)
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [awaitingKpay, setAwaitingKpay] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const priceOptions = {
    currencySymbol: paymentSettings.currencySymbol,
    locale: paymentSettings.priceLocale,
  };

  useEffect(() => {
    async function load() {
      if (!orderNum) return;
      setLoading(true);
      setError(null);
      try {
        const [orderRes, settingsRes] = await Promise.all([
          getPublicOrder(orderNum),
          getPublicPaymentSettings(),
        ]);

        if (!orderRes.success || !orderRes.data) {
          throw new Error(orderRes.error?.message || 'Order not found');
        }

        setOrder(orderRes.data);
        if (settingsRes.success && settingsRes.data) {
          setPaymentSettings(settingsRes.data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [orderNum]);

  // Returning from the KPay cashier: if this order was routed through KPay and
  // is still unpaid, poll the status endpoint until the webhook lands (the
  // notification can lag the redirect by a few seconds).
  useEffect(() => {
    if (!order || !orderNum) return;
    const isPayable = order.status === 'awaiting_payment' || order.status === 'pending';
    if (!isPayable || order.paymentProvider !== 'kpay') {
      setAwaitingKpay(false);
      return;
    }
    setAwaitingKpay(true);
    let attempts = 0;
    pollTimer.current = setInterval(async () => {
      attempts += 1;
      const res = await getKpayOrderStatus(orderNum);
      if (res.success && res.data?.paid) {
        if (pollTimer.current) clearInterval(pollTimer.current);
        const refreshed = await getPublicOrder(orderNum);
        if (refreshed.success && refreshed.data) setOrder(refreshed.data);
        setAwaitingKpay(false);
      } else if (attempts >= 36) {
        // ~3 min — stop polling; manual refresh shows the truth
        if (pollTimer.current) clearInterval(pollTimer.current);
        setAwaitingKpay(false);
      }
    }, 5000);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [order?.status, order?.paymentProvider, orderNum]);

  const handlePayNow = async () => {
    if (!order) return;
    setPaying(true);
    setPayError(null);
    try {
      const res = await createKpayCheckout(order.orderNum);
      if (!res.success || !res.data?.payUrl) {
        throw new Error(res.error?.message || '無法建立付款，請稍後再試');
      }
      window.location.href = res.data.payUrl;
    } catch (err) {
      setPayError(err instanceof Error ? err.message : String(err));
      setPaying(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !order) return;

    setUploading(true);
    setUploadSuccess(false);
    setError(null);

    try {
      const res = await uploadPaymentProof(order.id, file);
      if (!res.success) {
        throw new Error(res.error?.message || 'Upload failed');
      }
      setUploadSuccess(true);
      // Refresh order to show updated payment proofs
      const refreshed = await getPublicOrder(order.orderNum);
      if (refreshed.success && refreshed.data) {
        setOrder(refreshed.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-paper">
        <Header />
        <main className="pt-24 pb-20 px-4 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-brand-cinnabar" />
        </main>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-brand-paper">
        <Header />
        <main className="pt-24 pb-20 px-4">
          <div className="max-w-2xl mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-brand-cinnabar mx-auto mb-4" />
            <h1 className="text-2xl font-serif text-brand-ink mb-4">無法載入訂單</h1>
            <p className="text-brand-ink/60 mb-8">{error || 'Order not found'}</p>
            <Link to="/products" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-cinnabar text-brand-paper">
              <ChevronLeft className="w-4 h-4" />
              返回產品列表
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const isPaid = order.status === 'paid' || order.status === 'preparing' || order.status === 'shipped' || order.status === 'delivered';

  return (
    <div className="min-h-screen bg-brand-paper">
      <Header />
      <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <Link to="/products" className="inline-flex items-center gap-2 text-brand-ink/60 hover:text-brand-cinnabar">
              <ChevronLeft className="w-5 h-5" />
              繼續購物
            </Link>
          </div>

          <div className="bg-white border border-brand-ink/10 p-8 md:p-10 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
              <h1 className="text-2xl md:text-3xl font-serif text-brand-ink">{t('order.confirm_title')}</h1>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between border-b border-brand-ink/5 pb-3">
                <span className="text-brand-ink/60">{t('order.order_num')}</span>
                <span className="font-medium font-mono text-lg">{order.orderNum}</span>
              </div>
              <div className="flex justify-between border-b border-brand-ink/5 pb-3">
                <span className="text-brand-ink/60">{t('order.total')}</span>
                <span className="text-2xl font-bold text-brand-cinnabar">{formatPrice(order.totalAmount, priceOptions)}</span>
              </div>
              <div className="flex justify-between border-b border-brand-ink/5 pb-3">
                <span className="text-brand-ink/60">訂單狀態</span>
                <span className={`font-medium ${isPaid ? 'text-green-600' : 'text-brand-gold'}`}>
                  {isPaid ? t('order.paid') : t('order.pending')}
                </span>
              </div>
            </div>

            {!isPaid && (
              <>
                {/* KPay hosted cashier — instant online payment */}
                {paymentSettings.kpayEnabled && (
                  <div className="mb-8">
                    {awaitingKpay && (
                      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded flex items-center gap-2 text-amber-700">
                        <Clock className="w-5 h-5 animate-pulse" />
                        <span className="text-sm">付款處理中，正在確認結果…（付款成功後此頁會自動更新）</span>
                      </div>
                    )}
                    <button
                      onClick={handlePayNow}
                      disabled={paying}
                      className="w-full py-4 bg-brand-cinnabar text-brand-paper text-lg font-medium rounded hover:bg-brand-ink transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                      {paying ? '正在前往付款…' : '立即線上付款（FPS / PayMe / 信用卡）'}
                    </button>
                    {payError && (
                      <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {payError}
                      </p>
                    )}
                    <p className="mt-6 mb-2 text-sm text-brand-ink/40 text-center">或使用以下轉賬方式</p>
                  </div>
                )}

                <h2 className="text-xl font-serif text-brand-ink mb-4">付款方式</h2>
                <div className="space-y-4 mb-8">
                  {paymentSettings.paymentBankAccount && (
                    <div className="bg-brand-stone/20 p-4 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-brand-cinnabar" />
                        <span className="font-medium">{t('order.bank_transfer')}</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p>銀行：{paymentSettings.paymentBankName || '-'}</p>
                        <p className="flex items-center gap-2">
                          賬戶：{paymentSettings.paymentBankAccount}
                          <button
                            onClick={() => handleCopy(paymentSettings.paymentBankAccount || '', 'bank')}
                            className="text-brand-cinnabar hover:underline text-xs"
                          >
                            {copied === 'bank' ? '已複製' : '複製'}
                          </button>
                        </p>
                        <p>戶名：{paymentSettings.paymentPayeeName || '-'}</p>
                      </div>
                    </div>
                  )}

                  {paymentSettings.paymentFpsId && (
                    <div className="bg-brand-stone/20 p-4 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-brand-cinnabar" />
                        <span className="font-medium">{t('order.fps')}</span>
                      </div>
                      <div className="text-sm">
                        <p className="flex items-center gap-2">
                          FPS ID：{paymentSettings.paymentFpsId}
                          <button
                            onClick={() => handleCopy(paymentSettings.paymentFpsId || '', 'fps')}
                            className="text-brand-cinnabar hover:underline text-xs"
                          >
                            {copied === 'fps' ? '已複製' : '複製'}
                          </button>
                        </p>
                        <p>戶名：{paymentSettings.paymentPayeeName || '-'}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-brand-ink/10 pt-8">
                  <h2 className="text-xl font-serif text-brand-ink mb-4">{t('order.upload_proof')}</h2>

                  {order.paymentProofs && order.paymentProofs.length > 0 && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
                      <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle2 className="w-5 h-5" />
                        <span>已收到您的付款憑證，我們會盡快確認。</span>
                      </div>
                    </div>
                  )}

                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded cursor-pointer transition-colors ${uploading ? 'border-brand-ink/20 bg-brand-stone/20' : 'border-brand-ink/10 hover:border-brand-cinnabar hover:bg-brand-stone/10'}`}>
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className={`w-8 h-8 mb-2 text-brand-ink/40 ${uploading ? 'animate-bounce' : ''}`} />
                      <p className="text-sm text-brand-ink/60">
                        {uploading ? '上傳中…' : '點擊上傳付款憑證（圖片或 PDF）'}
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>

                  {uploadSuccess && (
                    <div className="mt-4 flex items-center gap-2 text-green-600 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      上傳成功
                    </div>
                  )}
                </div>
              </>
            )}

            {isPaid && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>付款已確認，我們將盡快安排發貨。</span>
                </div>
              </div>
            )}
          </div>

          {/* Order items summary */}
          <div className="bg-white border border-brand-ink/10 p-6 md:p-8">
            <h2 className="text-xl font-serif text-brand-ink mb-6">訂購產品</h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex justify-between items-center border-b border-brand-ink/5 pb-4 last:border-0">
                  <div>
                    <p className="font-medium text-brand-ink">{item.productName}</p>
                    <p className="text-sm text-brand-ink/50">{formatPrice(item.price, priceOptions)} × {item.quantity}</p>
                  </div>
                  <span className="font-serif">{formatPrice(item.price * item.quantity, priceOptions)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingActions />
    </div>
  );
};

export default OrderConfirmationPage;
